import { Link } from 'react-router-dom'
import { MapPin, Calendar } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { EventDocument, TicketTypeDocument } from '@/types'
import { formatPrice } from './TicketTypeSelector'

interface Props {
  event: EventDocument & { id: string }
  ticketTypes?: Array<TicketTypeDocument & { id: string }>
}

export function EventCard({ event, ticketTypes = [] }: Props) {
  const formattedDate = event.eventDate.toDate().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const formattedTime = event.eventDate.toDate().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })

  const visiblePrices = ticketTypes.filter((tt) => tt.isVisible && tt.price > 0).map((tt) => tt.price)
  const lowestPrice = visiblePrices.length > 0 ? Math.min(...visiblePrices) : null
  const hasFree = ticketTypes.some((tt) => tt.isVisible && tt.price === 0)

  return (
    <Link
      to={`/events/${event.slug}`}
      className="group block overflow-hidden rounded-xl border bg-card transition-shadow hover:shadow-md"
    >
      {event.coverUrl ? (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={event.coverUrl}
            alt={event.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      ) : (
        <div className="aspect-video w-full bg-muted" />
      )}

      <div className="p-4 space-y-2">
        <h3 className="font-semibold text-lg leading-tight group-hover:text-primary transition-colors">
          {event.title}
        </h3>

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4 shrink-0" />
          <span>
            {formattedDate} · {formattedTime}
          </span>
        </div>

        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0" />
          <span>{event.venue}</span>
        </div>

        <div className="pt-1">
          {hasFree && lowestPrice === null ? (
            <Badge variant="secondary">Free</Badge>
          ) : hasFree ? (
            <Badge variant="secondary">From Free</Badge>
          ) : lowestPrice !== null ? (
            <Badge variant="secondary">From {formatPrice(lowestPrice)}</Badge>
          ) : (
            <Badge variant="outline">Tickets Available</Badge>
          )}
        </div>
      </div>
    </Link>
  )
}
